import { useState, type ReactNode } from 'react';
import { Check, X, Plus, Copy, Clock, Link2, Video, Trash2, Loader2, LayoutList, CalendarDays } from 'lucide-react';
import { TimeDropdown } from '@/components/TimeDropdown';
import { DateInput } from '@/components/ui/DateInput';
import { SuperAdminDemoCalendar } from '@/components/super-admin/SuperAdminDemoCalendar';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';
import { SelectField } from '@/components/ui/SelectField';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin demo Availability — a faithful clone of the tenant Time
// Availability "Event Types + per-day availability" UI (NO vendor file used).
// Per-day multiple ranges, copy-to-days, totals, backed by /super-admin/demo.
// ────────────────────────────────────────────────────────────────────────────

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Range { start: string; end: string }
interface DayHours { enabled: boolean; ranges: Range[] }

const toMins = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const fmt12 = (t: string) => { const [h, m] = t.split(':').map(Number); const ap = h < 12 ? 'AM' : 'PM'; const hh = h % 12 || 12; return `${hh}:${String(m).padStart(2, '0')} ${ap}`; };
const addHour = (t: string) => { const mins = Math.min(toMins(t) + 60, 23 * 60 + 59); return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`; };
const diff = (s: string, e: string) => { const d = toMins(e) - toMins(s); if (d <= 0) return '0h'; const h = Math.floor(d / 60), m = d % 60; return `${h ? `${h}h` : ''}${m ? ` ${m}m` : ''}`.trim(); };
const dayTotal = (ranges: Range[]) => { const t = ranges.reduce((s, r) => s + Math.max(0, toMins(r.end) - toMins(r.start)), 0); const h = Math.floor(t / 60), m = t % 60; return `${h ? `${h}h` : ''}${m ? ` ${m}m` : ''}`.trim() || '0h'; };
const rangeErr = (s: string, e: string) => toMins(e) <= toMins(s) ? 'End must be after start' : '';
const overlapErr = (ranges: Range[], i: number) => {
  const a = ranges[i];
  for (let j = 0; j < ranges.length; j++) if (j !== i) { const b = ranges[j]; if (toMins(a.start) < toMins(b.end) && toMins(a.end) > toMins(b.start)) return 'Overlaps another range'; }
  return '';
};
const hasError = (weekly: DayHours[]) => weekly.some(d => d.enabled && d.ranges.some((r, i) => rangeErr(r.start, r.end) || overlapErr(d.ranges, i)));

interface Override { id: string; date: string; ranges: Range[]; reason?: string | null }

export interface DemoCfg {
  adminId: string;
  eventName: string;
  workingDays: number[];
  weeklyHours: Record<string, Range[]>;
  startTime: string;
  endTime: string;
  timezone: string;
  slotDuration: number;
  bufferBefore: number;
  bufferAfter: number;
  maxPerDay: number;
  advanceNotice: number;
  futureLimit: number;
  isActive: boolean;
  bookingLink: string;
  overrides?: Override[];
}

const TIMEZONES = [
  'Asia/Kolkata', 'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'America/Mexico_City', 'Europe/London', 'Europe/Berlin', 'Asia/Dubai',
  'Asia/Singapore', 'Australia/Sydney', 'UTC',
];
const DRAWER_INPUT = 'w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]';

function hydrate(cfg: DemoCfg): DayHours[] {
  return Array.from({ length: 7 }, (_, i) => {
    const enabled = cfg.workingDays.includes(i);
    const stored = cfg.weeklyHours?.[String(i)];
    const ranges = stored?.length ? stored.map(r => ({ ...r })) : (enabled ? [{ start: cfg.startTime || '09:00', end: cfg.endTime || '18:00' }] : []);
    return { enabled, ranges };
  });
}

// apiBase: '/super-admin/demo' = my own settings (default); pass
// '/super-admin/demo/admins/<id>' to manage another admin's availability.
export function SuperAdminDemoAvailability({ cfg, onSaved, apiBase = '/super-admin/demo' }: { cfg: DemoCfg; onSaved: (c: DemoCfg) => void; apiBase?: string }) {
  const [weekly, setWeekly] = useState<DayHours[]>(() => hydrate(cfg));
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [showDrawer, setShowDrawer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copyPopup, setCopyPopup] = useState<{ dayIdx: number; selected: number[] } | null>(null);

  const updateDay = (i: number, patch: Partial<DayHours>) => setWeekly(w => w.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const updateRange = (di: number, ri: number, patch: Partial<Range>) =>
    setWeekly(w => w.map((d, i) => i === di ? { ...d, ranges: d.ranges.map((r, j) => j === ri ? { ...r, ...patch } : r) } : d));
  const addRange = (di: number) => setWeekly(w => w.map((d, i) => {
    if (i !== di) return d;
    const lastEnd = d.ranges[d.ranges.length - 1]?.end ?? '09:00';
    return { ...d, ranges: [...d.ranges, { start: lastEnd, end: addHour(lastEnd) }] };
  }));
  const removeRange = (di: number, ri: number) => setWeekly(w => w.map((d, i) => i === di ? { ...d, ranges: d.ranges.filter((_, j) => j !== ri) } : d));
  const onEnd = (di: number, ri: number, start: string, newEnd: string) => {
    if (toMins(newEnd) <= toMins(start)) { const f = addHour(start); updateRange(di, ri, { end: f }); toast('success', `End time adjusted to ${fmt12(f)}`); }
    else updateRange(di, ri, { end: newEnd });
  };

  const applyCopy = () => {
    if (!copyPopup || copyPopup.selected.length === 0) { setCopyPopup(null); return; }
    const src = weekly[copyPopup.dayIdx].ranges;
    setWeekly(w => w.map((d, i) => copyPopup.selected.includes(i) ? { ...d, enabled: true, ranges: src.map(r => ({ ...r })) } : d));
    toast('success', `Times copied to ${copyPopup.selected.map(i => DAY_NAMES[i]).join(', ')}`);
    setCopyPopup(null);
  };

  const save = async () => {
    const workingDays = weekly.map((d, i) => d.enabled ? i : -1).filter(i => i >= 0);
    const weeklyHours: Record<string, Range[]> = {};
    weekly.forEach((d, i) => { if (d.enabled && d.ranges.length) weeklyHours[String(i)] = d.ranges; });
    const first = Object.values(weeklyHours)[0]?.[0];
    setSaving(true);
    try {
      const { data } = await saApi.patch(`${apiBase}/config`, {
        workingDays,
        weeklyHours,
        ...(first ? { startTime: first.start, endTime: first.end } : {}),
      });
      onSaved(data);
      toast('success', 'Availability saved');
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => { navigator.clipboard.writeText(cfg.bookingLink); toast('success', 'Booking link copied'); };

  const toggleActive = async () => {
    try { const { data } = await saApi.patch(`${apiBase}/config`, { isActive: !cfg.isActive }); onSaved(data); }
    catch (e) { toast('error', extractErrorMessage(e, 'Failed to update')); }
  };

  // ── Date-specific hours (overrides) ──────────────────────────────────────
  const overrides = cfg.overrides ?? [];
  const [showOvForm, setShowOvForm] = useState(false);
  const [ovBusy, setOvBusy] = useState(false);
  const [ovForm, setOvForm] = useState<{ date: string; ranges: Range[]; reason: string }>({ date: '', ranges: [{ start: '09:00', end: '18:00' }], reason: '' });

  const refetch = async () => { const { data } = await saApi.get(`${apiBase}/config`); onSaved(data); };

  const ovUpdateRange = (ri: number, patch: Partial<Range>) => setOvForm(f => ({ ...f, ranges: f.ranges.map((r, i) => i === ri ? { ...r, ...patch } : r) }));
  const ovAddRange = () => setOvForm(f => { const last = f.ranges[f.ranges.length - 1]?.end ?? '09:00'; return { ...f, ranges: [...f.ranges, { start: last, end: addHour(last) }] }; });
  const ovRemoveRange = (ri: number) => setOvForm(f => ({ ...f, ranges: f.ranges.filter((_, i) => i !== ri) }));

  const saveOverride = async () => {
    if (!ovForm.date) { toast('error', 'Pick a date'); return; }
    setOvBusy(true);
    try {
      await saApi.post(`${apiBase}/overrides`, { date: ovForm.date, ranges: ovForm.ranges, reason: ovForm.reason || undefined });
      toast('success', 'Date-specific hours saved');
      setShowOvForm(false);
      setOvForm({ date: '', ranges: [{ start: '09:00', end: '18:00' }], reason: '' });
      await refetch();
    } catch (e) { toast('error', extractErrorMessage(e, 'Failed to save')); }
    finally { setOvBusy(false); }
  };

  const deleteOverride = async (id: string) => {
    try { await saApi.delete(`${apiBase}/overrides/${id}`); await refetch(); }
    catch (e) { toast('error', extractErrorMessage(e, 'Failed to remove')); }
  };

  return (
    <div className="space-y-6" onClick={() => copyPopup && setCopyPopup(null)}>
      {/* ── Event Types ─────────────────────────────────────────────── */}
      <div>
        <h2 className="text-base font-bold text-slate-800 mb-3">Event Types</h2>
        <div className="flex gap-3 overflow-x-auto pb-1">
          <div
            onClick={() => setShowDrawer(true)}
            className="flex-shrink-0 w-56 bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            title="Edit event settings"
          >
            <div className="h-1.5 w-full bg-[#6366F1]" />
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={e => { e.stopPropagation(); toggleActive(); }}
                  className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${cfg.isActive ? 'bg-blue-600' : 'bg-slate-200'}`}
                  title={cfg.isActive ? 'Accepting bookings' : 'Paused'}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${cfg.isActive ? 'left-5' : 'left-0.5'}`} />
                </button>
                <button onClick={e => { e.stopPropagation(); copyLink(); }} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600" title="Copy booking link">
                  <Link2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-sm font-bold text-slate-900 leading-tight mb-3">{cfg.eventName}</p>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> Duration: {cfg.slotDuration} min</div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3.5" /> Buffer: {cfg.bufferAfter} min</div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500"><Video className="h-3.5 w-3.5" /> Zoom</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDrawer && <EventDrawer cfg={cfg} apiBase={apiBase} onClose={() => setShowDrawer(false)} onSaved={onSaved} />}

      {/* ── Availability (per-day) ─────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-slate-800">Availability <span className="text-xs font-normal text-slate-400">({cfg.timezone})</span></h2>
          <div className="inline-flex border border-slate-200 rounded-lg p-0.5 bg-slate-50">
            <button onClick={() => setView('list')} className={`px-3 h-8 rounded text-xs font-semibold inline-flex items-center gap-1.5 ${view === 'list' ? 'bg-white shadow text-[#6366F1]' : 'text-slate-500'}`}>
              <LayoutList className="h-3.5 w-3.5" /> List
            </button>
            <button onClick={() => setView('calendar')} className={`px-3 h-8 rounded text-xs font-semibold inline-flex items-center gap-1.5 ${view === 'calendar' ? 'bg-white shadow text-[#6366F1]' : 'text-slate-500'}`}>
              <CalendarDays className="h-3.5 w-3.5" /> Calendar
            </button>
          </div>
        </div>

        {view === 'calendar' && <SuperAdminDemoCalendar cfg={cfg} apiBase={apiBase} />}

        {view === 'list' && (
        <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-[24px_56px_1fr_72px_48px] gap-0 border-b border-slate-100 px-4 py-2.5 bg-slate-50">
            <div /><p className="text-[11px] font-semibold text-slate-500 uppercase">Day</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase">Hours</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase text-right">Total</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase text-center">Action</p>
          </div>
          {weekly.map((day, di) => (
            <div key={di} className={`grid grid-cols-[24px_56px_1fr_72px_48px] gap-0 px-4 py-3 border-b border-slate-50 last:border-0 ${!day.enabled ? 'bg-slate-50/50' : ''}`}>
              <div className="flex items-start pt-0.5">
                <button
                  onClick={() => updateDay(di, { enabled: !day.enabled, ranges: !day.enabled && day.ranges.length === 0 ? [{ start: '09:00', end: '18:00' }] : day.ranges })}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center ${day.enabled ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}
                >
                  {day.enabled && <Check className="h-2.5 w-2.5 text-white" />}
                </button>
              </div>
              <div className="flex items-start pt-0.5"><span className={`text-xs font-bold ${day.enabled ? 'text-slate-800' : 'text-slate-400'}`}>{DAY_NAMES[di]}</span></div>
              <div className="space-y-1.5">
                {!day.enabled ? (
                  <span className="text-xs text-slate-400 italic">Unavailable</span>
                ) : (
                  <>
                    {day.ranges.map((r, ri) => {
                      const err = rangeErr(r.start, r.end) || overlapErr(day.ranges, ri);
                      return (
                        <div key={ri}>
                          <div className="flex items-center gap-1.5">
                            <TimeDropdown value={r.start} onChange={v => updateRange(di, ri, { start: v })} className="w-28" />
                            <span className="text-slate-400 text-xs">–</span>
                            <TimeDropdown value={r.end} onChange={v => onEnd(di, ri, r.start, v)} className="w-28" />
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium min-w-[40px] text-center ${err ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-400'}`}>{err ? 'Invalid' : diff(r.start, r.end)}</span>
                            {day.ranges.length > 1 && <button onClick={() => removeRange(di, ri)} className="text-slate-300 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>}
                          </div>
                          {err && <p className="text-[11px] text-red-500 mt-0.5">{err}</p>}
                        </div>
                      );
                    })}
                    <button onClick={() => addRange(di)} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"><Plus className="h-3 w-3" /> Add time range</button>
                  </>
                )}
              </div>
              <div className="flex items-start justify-end pt-0.5"><span className={`text-xs font-medium ${day.enabled && day.ranges.length ? 'text-slate-600' : 'text-slate-400'}`}>{day.enabled && day.ranges.length ? dayTotal(day.ranges) : '—'}</span></div>
              <div className="flex items-start justify-center pt-0.5 relative">
                {day.enabled && (
                  <button onClick={e => { e.stopPropagation(); setCopyPopup(copyPopup?.dayIdx === di ? null : { dayIdx: di, selected: [] }); }} title="Copy times to other days" className={`p-1 rounded ${copyPopup?.dayIdx === di ? 'text-emerald-700 bg-emerald-100' : 'text-emerald-500 hover:bg-emerald-50'}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
                {copyPopup?.dayIdx === di && (
                  <div onClick={e => e.stopPropagation()} className="absolute right-0 top-8 z-20 w-40 bg-white rounded-lg border border-slate-200 shadow-xl p-2">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase px-1 mb-1">Copy to</p>
                    {DAY_NAMES.map((n, i) => i !== di && (
                      <label key={i} className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 rounded">
                        <input type="checkbox" checked={copyPopup.selected.includes(i)} onChange={e => setCopyPopup(p => p ? { ...p, selected: e.target.checked ? [...p.selected, i] : p.selected.filter(x => x !== i) } : p)} />
                        {n}
                      </label>
                    ))}
                    <button onClick={applyCopy} className="w-full mt-1 px-2 py-1 rounded bg-blue-600 text-white text-xs font-semibold">Apply</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={save} disabled={saving || hasError(weekly)} className="px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Availability'}
          </button>
        </div>
        </div>

        {/* Specific Date (date-specific hours) */}
        <div className="lg:w-[360px] flex-shrink-0">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-slate-800">Specific Date</p>
              <button onClick={() => setShowOvForm(v => !v)} className="inline-flex items-center gap-1 px-3 h-8 rounded-lg border border-[#6366F1] text-[#6366F1] hover:bg-indigo-50 text-xs font-semibold">
                <Plus className="h-3.5 w-3.5" /> Add date-specific hours
              </button>
            </div>

            {showOvForm && (
              <div className="border border-slate-100 rounded-xl p-4 mb-3 space-y-3 bg-slate-50/60">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600 mb-1 block">Date</span>
                  <DateInput value={ovForm.date} onChange={v => setOvForm(f => ({ ...f, date: v }))} className="w-full" />
                </label>

                <div>
                  <div className="flex gap-2 mb-1">
                    <span className="text-xs font-semibold text-slate-600 flex-1">Start</span>
                    <span className="w-4" />
                    <span className="text-xs font-semibold text-slate-600 flex-1">End</span>
                    <span className="w-4" />
                  </div>
                  {ovForm.ranges.map((r, ri) => (
                    <div key={ri} className="flex items-center gap-2 mb-1.5">
                      <div className="flex-1"><TimeDropdown value={r.start} onChange={v => ovUpdateRange(ri, { start: v })} className="w-full" /></div>
                      <span className="text-slate-400 text-xs">–</span>
                      <div className="flex-1"><TimeDropdown value={r.end} onChange={v => ovUpdateRange(ri, { end: v })} className="w-full" /></div>
                      {ovForm.ranges.length > 1
                        ? <button onClick={() => ovRemoveRange(ri)} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                        : <span className="w-4" />}
                    </div>
                  ))}
                  <button onClick={ovAddRange} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-1"><Plus className="h-3 w-3" /> Add time range</button>
                </div>

                <input value={ovForm.reason} onChange={e => setOvForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason (optional)" className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]" />
                <p className="text-[11px] text-slate-400">Leave no time ranges to mark the date unavailable.</p>

                <div className="flex items-center gap-3 pt-1">
                  <button onClick={saveOverride} disabled={ovBusy} className="flex-1 h-10 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
                    {ovBusy && <Loader2 className="h-4 w-4 animate-spin" />} Save
                  </button>
                  <button onClick={() => setShowOvForm(false)} className="text-sm text-slate-500 hover:text-slate-700 px-2">Cancel</button>
                </div>
              </div>
            )}

            {overrides.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No date-specific hours set</p>
            ) : (
              <div className="space-y-2">
                {overrides.map(o => (
                  <div key={o.id} className="flex items-start justify-between gap-2 border border-slate-100 rounded-lg p-2.5">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-800">{o.date}</p>
                      <p className="text-[11px] text-slate-500">{o.ranges.length ? o.ranges.map(r => `${fmt12(r.start)}–${fmt12(r.end)}`).join(', ') : 'Unavailable'}</p>
                    </div>
                    <button onClick={() => deleteOverride(o.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
        )}
      </div>
    </div>
  );
}

function DField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function EventDrawer({ cfg, apiBase, onClose, onSaved }: { cfg: DemoCfg; apiBase: string; onClose: () => void; onSaved: (c: DemoCfg) => void }) {
  const [form, setForm] = useState({
    eventName: cfg.eventName,
    timezone: cfg.timezone,
    slotDuration: cfg.slotDuration,
    bufferBefore: cfg.bufferBefore,
    bufferAfter: cfg.bufferAfter,
    maxPerDay: cfg.maxPerDay,
    advanceNotice: cfg.advanceNotice,
    futureLimit: cfg.futureLimit,
  });
  const [saving, setSaving] = useState(false);
  const set = (p: Partial<typeof form>) => setForm(f => ({ ...f, ...p }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await saApi.patch(`${apiBase}/config`, {
        eventName: form.eventName,
        timezone: form.timezone,
        slotDuration: Number(form.slotDuration),
        bufferBefore: Number(form.bufferBefore),
        bufferAfter: Number(form.bufferAfter),
        maxPerDay: Number(form.maxPerDay),
        advanceNotice: Number(form.advanceNotice),
        futureLimit: Number(form.futureLimit),
      });
      onSaved(data);
      toast('success', 'Event settings saved');
      onClose();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Event settings</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <DField label="Event name">
            <input value={form.eventName} onChange={e => set({ eventName: e.target.value })} className={DRAWER_INPUT} placeholder="Product Demo" />
          </DField>
          <DField label="Timezone">
            <SelectField value={form.timezone} onChange={e => set({ timezone: e.target.value })} className={DRAWER_INPUT}>
              {(TIMEZONES.includes(form.timezone) ? TIMEZONES : [form.timezone, ...TIMEZONES]).map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </SelectField>
          </DField>
          <div className="grid grid-cols-2 gap-3">
            <DField label="Slot duration (min)"><input type="number" min={5} max={480} value={form.slotDuration} onChange={e => set({ slotDuration: +e.target.value })} className={DRAWER_INPUT} /></DField>
            <DField label="Max demos / day"><input type="number" min={1} max={100} value={form.maxPerDay} onChange={e => set({ maxPerDay: +e.target.value })} className={DRAWER_INPUT} /></DField>
            <DField label="Buffer before (min)"><input type="number" min={0} max={240} value={form.bufferBefore} onChange={e => set({ bufferBefore: +e.target.value })} className={DRAWER_INPUT} /></DField>
            <DField label="Buffer after (min)"><input type="number" min={0} max={240} value={form.bufferAfter} onChange={e => set({ bufferAfter: +e.target.value })} className={DRAWER_INPUT} /></DField>
            <DField label="Advance notice (min)"><input type="number" min={0} max={10080} value={form.advanceNotice} onChange={e => set({ advanceNotice: +e.target.value })} className={DRAWER_INPUT} /></DField>
            <DField label="Bookable up to (days · 0 = unlimited)"><input type="number" min={0} max={3650} value={form.futureLimit} onChange={e => set({ futureLimit: Math.max(0, +e.target.value) })} className={DRAWER_INPUT} /></DField>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
